import { useDispatch, useSelector } from "react-redux";
import { useCallback, useState, useRef, useEffect } from "react";
import BuilderSelect from "@/store/builderStoreSelector";
import { fabric } from 'fabric';
import { v4 as uuid } from 'uuid';
import TwoDWallLayoutBuilder from "./TwoDWallLayoutBuilder";
import { TwoDMesh, TwoDRoomLayout, TwoDUi } from "@/store/builderSlice";
import { actions as builderActions } from "@/store/builderSlice";
import TwoDWallRenderer from "./TwoDWallRenderer";
import { useDrop } from "react-dnd";
import { Box, Tooltip, Typography } from "@mui/material";
import ClientComponent from "../client_component";
import { findEndpoints, intersectOfLines } from "@/utils/builder/twoDRoomLayoutUtils";
import { BUILDER_SUCCESS, DimensionalViewType, TRIGGER_ROOM_LAYOUT_THUMBNAILS_SAVE } from "@/utils/builder/builderConstants";
import { saveRoomLayoutThumbnail } from "@/store/builder/thunks/roomModelsThunk";
import CloseIcon from '@mui/icons-material/Close';
import { gtmClickTracking } from "@/utils";
import { log } from "console";

export interface TwoDRoomLayoutBuilderProps {

}

const TwoDRoomLayoutBuilder: React.FC<TwoDRoomLayoutBuilderProps> = (
) => {
    const dispatch = useDispatch();
    const canvasElementRef = useRef<HTMLCanvasElement | null>(null);
    const [value, setValue] = useState<number | undefined>(0);
    const fabricCanvasObjRef = useRef<fabric.Canvas | null>(null);
    const transformX = useRef(0);
    const transformY = useRef(0);
    const [isCanvasReady, setIsCanvasReady] = useState(false);
    const twoDRoomLayout: TwoDRoomLayout = BuilderSelect.twoDRoomLayout();
    //let showSelectWallTooltip = BuilderSelect.twoDUi_showSelectWallTooltip();
    const urlParams: { [key: string]: string } = BuilderSelect.ui().urlParams;
    //in 3d builder, quickly SelectWallTooltip is showing and disappearing so i added addtional check(when projectId  is present in urlParams that means it is in 3d builder so it doesn't show any tooltips)
    const [showSelectWallTooltip, setShowSelectWallTooltip] = useState(true && (!urlParams.projectId && !urlParams.showcase));
    const showLayoutInfoTooltip = BuilderSelect.twoDUi_showLayoutInfoTooltip();
    const layoutBuilderMode: string = BuilderSelect.ui().layoutBuilderMode;
    const canvasTopOffset = 65;
    const canvasWidth = document.documentElement.clientWidth, canvasHeight = document.documentElement.clientHeight - canvasTopOffset;
    const twoDRoomLayoutRef = useRef<TwoDRoomLayout>(twoDRoomLayout);
    const unitsScale = useRef(1);
    const xOffset = useRef(0);
    const yOffset = useRef(0);
    const [infoTooltipPosition, setInfoTooltipPosition] = useState({ x: 0, y: 0 });
    const workspaceDimens = 20;
    const wallTooltipPosition = { x: 0, y: 0 };
    // this is to consider whole view port as 20 ft, 
    //so that we try to arrange the room according to this
    twoDRoomLayoutRef.current = twoDRoomLayout;

    const addObjectToCanvas = (assetType: string, subType: string, left: number, top: number) => {
        const thumbnailMouseYOffset = 50;
        const thumbnailMouseXOffset = 50;
        const rect = new fabric.Rect({
            left: left - transformX.current + thumbnailMouseXOffset,
            top: top - (transformY.current + canvasTopOffset) + thumbnailMouseYOffset,
            originX: 'center',
            originY: 'center',
            fill: 'blue',
            height: 100,
            width: 100,
            hasControls: false,
            lockMovementX: true,
            lockMovementY: true,
            lockRotation: true,
            opacity: 1
        });

        fabricCanvasObjRef.current?.add(rect);
        const objects = fabricCanvasObjRef.current?.getObjects();
        const droppedWalls = objects!.filter(object => {
            let isDroppedOnWall = false;
            if (object.name && object.name.indexOf('wallContainerRect_') !== -1) {
                isDroppedOnWall = object.intersectsWithObject(rect, false, true);
            }
            return isDroppedOnWall;
        });
        fabricCanvasObjRef.current?.remove(rect);
        if (droppedWalls.length > 0) {
            const currentWall = droppedWalls[0];
            const wallEndPoints = findEndpoints(currentWall.left!, currentWall.top!, currentWall.angle!, currentWall.width!);
            let currentObjP1 = { x: 0, y: 0 };
            let currentObjP2 = { x: 0, y: 0 };
            const thumbnailWidth = 100;
            if (Math.abs(currentWall.angle!) < 45) {
                currentObjP1 = {
                    x: left - transformX.current,
                    y: top - (transformY.current + canvasTopOffset) + thumbnailMouseYOffset - (thumbnailWidth / 2)
                };
                currentObjP2 = {
                    x: currentObjP1.x,
                    y: currentObjP1.y + thumbnailWidth
                };
            } else {
                currentObjP1 = {
                    x: left - transformX.current - (thumbnailWidth / 2),
                    y: top - (transformY.current + canvasTopOffset) + thumbnailMouseYOffset,
                };
                currentObjP2 = {
                    x: currentObjP1.x + thumbnailWidth + thumbnailMouseXOffset,
                    y: currentObjP1.y
                };
            }

            const intersectPoint = intersectOfLines(
                wallEndPoints.endpoint1.x,
                wallEndPoints.endpoint1.y,
                wallEndPoints.endpoint2.x,
                wallEndPoints.endpoint2.y,
                currentObjP1.x,
                currentObjP1.y,
                currentObjP2.x,
                currentObjP2.y
            );

            if (intersectPoint) {
                let mesh = {
                    id: uuid(),
                    type: assetType,
                    subType: subType,
                    position: {
                        x: intersectPoint.x / unitsScale.current,
                        y: intersectPoint.y / unitsScale.current
                    },
                    rotation: currentWall.angle!,
                    width: assetType === 'door' ? 2.5 : 3

                } as TwoDMesh;
                dispatch(builderActions.addTwoDWallMeshChild({
                    //@ts-ignore
                    wallId: currentWall.wallId,
                    wallMesh: mesh,
                    undoable: true
                }));
            }
        }
    }
    const [{ isOver, isOverCurrent }, drop] = useDrop(
        () => ({
            accept: 'box',
            drop(_item: any, monitor) {
                const mouseUpPoint = monitor.getClientOffset();
                const initialMouseUpPoint = monitor.getInitialSourceClientOffset();
                addObjectToCanvas(_item.assetType, _item.type, mouseUpPoint!.x, mouseUpPoint!.y);
            },
            collect: (monitor) => ({
                isOver: monitor.isOver(),
                isOverCurrent: monitor.isOver({ shallow: true }),
            }),
        }),
        [],
    )

    const onBeforeCanvasRender = () => {
        if (!fabricCanvasObjRef.current) return;
        const desiredObjects: fabric.Object[] = [];
        const objects = fabricCanvasObjRef.current.getObjects();
        objects.forEach(object => {
            //@ts-ignore
            if (object.rectType === 'door' || object.rectType === 'window') {
                //@ts-ignore
                const wallId = object.wallId;
                //@ts-ignore
                const meshId = object.rectId;
                const wall = twoDRoomLayoutRef.current.walls.find(wall => wall.id === wallId);
                if (wall) {
                    const child = wall.children.find(child => child.id === meshId);
                    if (child)
                        desiredObjects.push(object);
                }
            } else {
                desiredObjects.push(object);
            }
        });
        const reRender = objects.length !== desiredObjects.length;
        fabricCanvasObjRef.current._objects = desiredObjects;
        if (reRender) fabricCanvasObjRef.current.requestRenderAll();
        //console.log('canvas- objects : ', objects);
    }

    const drawGridLines = (canvas: fabric.Canvas) => {
        const imageUrl = "./env/grid.jpg";
        const background = new Image();
        background.src = imageUrl;

        // background.onload = function () {
        //     canvas.setBackgroundImage(imageUrl, canvas.renderAll.bind(canvas), {
        //         left: -600,
        //         top: -200,
        //         scaleX: 1.3,
        //         scaleY: 1.3
        //     });
        // };

        const dot = new fabric.Circle({
            left: 0,
            top: 0,
            radius: 20,
            fill: 'red',
            objectCaching: false
        });
        //canvas.add(dot);

        // const startX = -450;
        // const startY = -85;
        const dots = [];
        const lines = [];
        for (let col = 0; col < (canvasHeight - 50) / workspaceDimens; col++) {
            for (let row = 0; row < (canvasWidth - 60) / workspaceDimens; row++) {
                const dot = new fabric.Circle({
                    left: row * 30,
                    top: col * 30,
                    radius: 3,
                    fill: '#808B96',
                    objectCaching: false
                });
                dots.push(dot);
            }
        }

        for (let col = 0; col * 10 < canvasHeight; col++) {
            const line = new fabric.Line([0, col * 10, canvasWidth, col * 10], {
                stroke: 'black',
                strokeWidth: 0.1
            });
            lines.push(line);
        }

        for (let col = 0; col * 40 < canvasHeight; col++) {
            const line = new fabric.Line([0, col * 40, canvasWidth, col * 40], {
                stroke: 'black',
                strokeWidth: 0.3
            });
            lines.push(line);
        }

        for (let row = 0; row * 10 < canvasWidth; row++) {
            const line = new fabric.Line([row * 10, 0, row * 10, canvasHeight], {
                stroke: 'black',
                strokeWidth: 0.1
            });
            lines.push(line);
        }
        for (let row = 0; row * 40 < canvasWidth; row++) {
            const line = new fabric.Line([row * 40, 0, row * 40, canvasHeight], {
                stroke: 'black',
                strokeWidth: 0.3
            });
            lines.push(line);
        }


        const oGridGroup = new fabric.Group(lines, { left: -transformX.current, top: -transformY.current, selectable: false });
        oGridGroup.name = 'dotsGrid';
        canvas.insertAt(oGridGroup, 0, false);
    }

    const initCanvasEvents = (canvas: fabric.Canvas) => {
        canvas.on('before:render', onBeforeCanvasRender);
        canvas.on('selection:created', resetTooltip);
        canvas.on('selection:updated', updateSelection);
        canvas.on('selection:cleared', clearSelection);
    }
    useEffect(() => {
        fabricCanvasObjRef.current = new fabric.Canvas(canvasElementRef.current, {
            preserveObjectStacking: true,
            backgroundColor: '#99A3A4'
        });
        fabricCanvasObjRef.current.viewportTransform = [1, 0, 0, 1, transformX.current, transformY.current];
        fabricCanvasObjRef.current.perPixelTargetFind = true;
        let minX = Infinity, minY = Infinity;
        twoDRoomLayout.vertices.forEach(vertex => {
            if (vertex.x < minX) minX = vertex.x;
            if (vertex.y < minY) minY = vertex.y;
        });
        xOffset.current = minX;
        yOffset.current = minY;

        //canvasElementRef.current.style.top = '15px';
        //canvasElementRef.current.style.left = '180px';
        initCanvasEvents(fabricCanvasObjRef.current);
        //drawGridLines(fabricCanvasObjRef.current);
        setIsCanvasReady(true);
        //window.twoDCanvas = fabricCanvasObjRef.current;
        unitsScale.current = Math.floor(canvasHeight / workspaceDimens);
        if (canvasWidth < canvasHeight)
            unitsScale.current = Math.floor(canvasWidth / workspaceDimens);
        console.log('debug-roomBoundingRect :: ', canvasWidth, canvasHeight, unitsScale.current);
        fabricCanvasObjRef.current.renderAll();

        return () => {
            if (fabricCanvasObjRef.current) {
                fabricCanvasObjRef.current.clear();
                fabricCanvasObjRef.current.dispose();
            }
        }
    }, []);

    useEffect(() => {
        console.log('update dimens:: ', canvasWidth, canvasHeight, unitsScale.current);
        fabricCanvasObjRef.current?.setWidth(canvasWidth);
        fabricCanvasObjRef.current?.setHeight(canvasHeight);
    }, [canvasWidth, canvasHeight]);

    useEffect(() => {
        if (twoDRoomLayout.saveStatus === TRIGGER_ROOM_LAYOUT_THUMBNAILS_SAVE && fabricCanvasObjRef.current) {
            const data = fabricCanvasObjRef.current.toDataURL();
            dispatch(saveRoomLayoutThumbnail({ image: data }) as any);
        }
    }, [twoDRoomLayout.saveStatus]);

    useEffect(() => {
        if (twoDRoomLayout.captureThumbnailStatus === 'start' && fabricCanvasObjRef.current) {
            const data = fabricCanvasObjRef.current.toDataURL();
            dispatch(builderActions.saveRoomLayoutThumbnailLocally({
                image: data
            }));
        }
    }, [twoDRoomLayout.captureThumbnailStatus]);

    const adjustCanvasPosition = useCallback((roomBoundingRect: any) => {
        if (!fabricCanvasObjRef.current) return;
        transformX.current = (canvasWidth - roomBoundingRect.width) / 2;
        transformY.current = (canvasHeight - roomBoundingRect.height) / 2;
        setInfoTooltipPosition({ x: transformX.current + roomBoundingRect.width / 2, y: transformY.current + roomBoundingRect.height / 2 + 65 });
        fabricCanvasObjRef.current.viewportTransform = [1, 0, 0, 1, transformX.current, transformY.current];
        drawGridLines(fabricCanvasObjRef.current);
    }, []);


    const resetTooltip = () => {
        dispatch(builderActions.updatCurrentTab({
            currentTab: ''
        }));
        dispatch(builderActions.updateTwoDUi({
            showSelectWallTooltip: false
        }));
    }

    const clearSelection = () => {
        resetTooltip();
        dispatch(builderActions.updateTwoDUi({
            showLayoutInfoTooltip: true
        }));
        dispatch(builderActions.updateActiveWallId({
            activeWallId: ''
        }));
    }

    const updateSelection = () => {
        resetTooltip();
        dispatch(builderActions.updateTwoDUi({
            showLayoutInfoTooltip: true
        }));
    }

    const childComps = [];
    if (isCanvasReady && fabricCanvasObjRef.current) {
        const vertices = twoDRoomLayout.vertices.map(vertex => Object.assign({}, vertex));
        childComps.push(<TwoDWallLayoutBuilder
            canvas={fabricCanvasObjRef.current}
            points={vertices}
            adjustCanvasPosition={adjustCanvasPosition}
            unitsScale={unitsScale.current}
            wallColor={twoDRoomLayout.walls?.[0]?.color || 'gray'}
            floorTextureId={twoDRoomLayout.floorTextureId}
            xOffset={xOffset.current}
            yOffset={yOffset.current}
        />
        );
        twoDRoomLayout.walls.forEach((wallMesh, index) => {
            if (index === 0) {
                wallTooltipPosition.x = transformX.current + wallMesh.position.x * unitsScale.current;
                wallTooltipPosition.y = canvasTopOffset + transformY.current + wallMesh.position.y * unitsScale.current;
            }
            childComps.push(<TwoDWallRenderer
                key={wallMesh.id}
                canvas={fabricCanvasObjRef.current!}
                wall={wallMesh}
                unitsScale={unitsScale.current}
                xOffset={xOffset.current}
                yOffset={yOffset.current}
            />);
        })
    }

    const getWindowDimensions = () => {
        const { innerWidth: width, innerHeight: height } = window;
        return {
          width,
          height
        };
      };
      const useWindowDimensions = () => {
        const [windowDimensions, setWindowDimensions] = useState(
          getWindowDimensions()
        );
        useEffect(() => {
          function handleResize() {
            setWindowDimensions(getWindowDimensions());
          }
          window.addEventListener("resize", handleResize);
          return () => window.removeEventListener("resize", handleResize);
        }, []);
        return windowDimensions;
      };

      const { height, width } = useWindowDimensions();

      useEffect(() => {
        if (width < 768) {
          setValue(undefined);
        }
      }, []);

    return <>
       
        <Box component="div"
            sx={{
                position: 'absolute', // Make sure to position it correctly
                zIndex: 99999999,
                width: width < 768 ? '90%' : '25%',
                left: width < 768 ? '5%' : '1%', // Position based on your state
                bottom: width < 768 ? '80px' : undefined, // Adjust bottom position as needed
                top: width > 768 ? '90px' : undefined,
                opacity: 1.0,
                display: showSelectWallTooltip ? 'block' : 'none', // Show or hide based on state
            }}
        >
            <Box component="div"
                sx={{
                    backgroundColor: 'white',
                    paddingTop: '20px',
                    paddingBottom: '20px',
                    paddingLeft: '40px',
                    paddingRight: '40px',
                    position: 'relative',
                    borderRadius: '30px',
                    boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
                }}
            >
                <Typography fontSize={18} color={'#52575C'} fontFamily={'Nunito'} fontWeight={600} align="center">
                    Customize your room
                <br />
                </Typography>

                <Typography fontSize={12} color={'black'} fontFamily={'Nunito'} align="center" sx={{ marginTop: '16px' }}>
                    Tap a wall to add or change doors, windows, or colors. Drag the corners to change dimensions and tap the floor to change the material.
                </Typography>

                {/* Close Button */}
                <Box component="div"
                sx={{
                    position: 'absolute',
                    width: '16px',
                    height: '16px',
                    top: '15px',
                    right: '20px',
                    cursor: 'pointer',
                }}
                onClick={() => {
                    dispatch(builderActions.updateTwoDUi({
                        showSelectWallTooltip: false,
                    }));
                    console.log('setShowSelectWallTooltip', showSelectWallTooltip);
                    
                    setShowSelectWallTooltip(false);
                    gtmClickTracking({
                        event: "Close_Info",
                        pageName: "Builder 2D Edit",
                    });
                }} 
                >
                <CloseIcon fontSize="large" color="action" />
                </Box>
            </Box>
        </Box>

        <button
            style={{
                position: 'fixed',
                left: `${infoTooltipPosition.x - 20}px`,
                top: `${infoTooltipPosition.y - 40}px`,   
                width: '40px', // Match the width and height of the tooltip
                height: '40px',
                borderRadius: '50%', // Make button circular
                border: '0px',
                // backgroundColor: 'white', // White background
                // color: 'black', 
                // cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center', // Center text inside the button
                fontSize: '13px', // Font size for button text
                fontFamily: 'Nunito', // Optional: Apply your preferred font
                fontWeight: '600',
                zIndex: 999
            }}
            onClick={() => {
                
                dispatch(builderActions.updatCurrentTab({
                    currentTab: 'floorColorTab'
                }));
                dispatch(builderActions.updateActiveWallId({
                    activeWallId: ''
                }));
            }}
            >
            Floor
        </button>

        <button
            style={{
                position: 'fixed',
                left: `20px`,
                top: `100px`,   
                width: '24px', // Match the width and height of the tooltip
                height: '24px',
                borderRadius: '50%', // Make button circular
                backgroundColor: 'white', // White background
                color: 'black', 
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center', // Center text inside the button
                fontSize: '12px', // Font size for button text
                fontFamily: 'Nunito', // Optional: Apply your preferred font
                zIndex: 999
            }}
            onClick={() => {
                console.log('SelectWallToolTip ----> ', showLayoutInfoTooltip);
                dispatch(builderActions.updateActiveWallId({
                    activeWallId: ''
                }));
                dispatch(builderActions.updatCurrentTab({
                    currentTab: ''
                }));
                setShowSelectWallTooltip(true);

                gtmClickTracking({
                    event: "Open_Info",
                    pageName: "Builder 2D Edit",
                });
            }}
            >
                <img
                    src="icons/Info.svg"    // Source of your SVG image
                    alt="Button Icon"  // Accessibility for screen readers
                    style={{
                        width: '24px',   // Set width of the icon
                        height: '24px',  // Set height of the icon
                    }}
                />
        </button>

        <Box id="roomView_2D" component={'div'} ref={drop}>
            <canvas ref={canvasElementRef} width={canvasWidth} height={canvasHeight} >
                {childComps}
            </canvas>
        </Box>
    </>
}

export default ClientComponent(TwoDRoomLayoutBuilder);